import { Bowser } from './bowser';

export type DetectedOs = 'ios' | 'android' | 'other' | 'unknown';

/** 出し分けの対象となるOS。下の対応表はいずれもこれだけを返す */
type MobileOs = Extract<DetectedOs, 'ios' | 'android'>;

/**
 * UA文字列から判る範囲。判定不能は `null` で表すので `unknown` は含めない。
 * 'macos' は iPadOS のデスクトップ表示と区別が付かない状態を表す中間の値で、外には出ない。
 */
type OsFromUserAgent = MobileOs | 'macos' | 'other';

/**
 * Bowserが返すOS名 → アプリ内の語彙。
 *
 * キーをライブラリの定数から引くことで、Bowser側の表記（'iOS' / 'Android'）が変わっても追従する。
 * ここに載っていないOS（Windows / Linux 等）は 'other' として扱う。
 */
const OS_NAME_TO_DETECTED: Record<string, OsFromUserAgent | undefined> = {
  [Bowser.OS_MAP.iOS]: 'ios',
  [Bowser.OS_MAP.Android]: 'android',
  // macOS は iPadOS のデスクトップ表示が紛れ込むため、ここでは確定させずタッチ判定に回す
  [Bowser.OS_MAP.MacOS]: 'macos'
};

/**
 * Client Hints の platform 値 → アプリ内の語彙。キーは正規化（unquote + 小文字化）後の値。
 *
 * macOS / Windows / Linux をここに載せて 'other' を確定させると
 * iPadOS のデスクトップ表示を取りこぼすため、意図的に iOS / Android だけを扱う。
 */
const CLIENT_HINTS_PLATFORM_TO_DETECTED: Record<string, MobileOs | undefined> = {
  android: 'android',
  ios: 'ios'
};

/**
 * OS判定に使うシグナル。
 * クライアント/サーバのどちらからでも渡せるようにするため、DOMには依存しない。
 */
export interface DetectOsSignals {
  userAgent: string;
  /**
   * Client Hints のプラットフォーム。
   * - クライアント: `navigator.userAgentData?.platform`（例: 'Android'）
   * - サーバ:       `Sec-CH-UA-Platform` ヘッダ（例: '"Android"' とクォート付きで届く）
   *
   * UA文字列より偽装されにくいため最優先で見る。Safari / Firefox は送らないので undefined になる。
   */
  clientHintsPlatform?: string | null;
  /**
   * `navigator.maxTouchPoints`。iPadOS のデスクトップ表示判定にのみ使う。
   * サーバでは取得できないため、サーバサイドではこの判定が働かない（macOSとして扱われる）。
   */
  maxTouchPoints?: number | null;
}

/**
 * Client Hints の platform 値を比較用に整える。
 * サーバ経由（`Sec-CH-UA-Platform` ヘッダ）だと `"Android"` のようにクォートが付く。
 */
function parseClientHintsPlatform(value: string | null | undefined): string {
  return (value ?? '').trim().replace(/^"|"$/g, '').toLowerCase();
}

/** 判定できなければ `null`。'macOS' 等を 'other' に確定させないのは後段のiPad判定に委ねるため */
function detectByClientHints(clientHintsPlatform: string | null | undefined): MobileOs | null {
  return CLIENT_HINTS_PLATFORM_TO_DETECTED[parseClientHintsPlatform(clientHintsPlatform)] ?? null;
}

/** UAが空、またはBowserがOSを特定できなければ `null` */
function detectByUserAgent(userAgent: string): OsFromUserAgent | null {
  if (!userAgent) {
    return null;
  }

  let osName: string | undefined;
  try {
    // `getOSName(true)` は未知のUAに対して**文字列 'undefined'** を返すため使わないこと。
    // それを未知のOS名として扱うと 'other' に落ち、'unknown' フォールバックが機能しなくなる。
    osName = Bowser.getParser(userAgent).getOS().name;
  } catch {
    // Bowserは空UAで例外を投げる。判定不能として扱い、呼び出し側のフォールバックに委ねる。
    return null;
  }

  if (!osName) {
    return null;
  }

  return OS_NAME_TO_DETECTED[osName] ?? 'other';
}

/**
 * iPadOS 13以降は Safari が `Macintosh` を名乗るため、UA文字列だけでは macOS と区別できない。
 * Macにはタッチスクリーンが無いことを利用して見分ける。
 *
 * この判定は **Bowserが macOS と判定した場合にのみ** 適用すること（呼び出し側で担保している）。
 * `maxTouchPoints` はAndroid端末でも 5 前後を返し、Windowsのタッチ対応機も同様のため、
 * OSを絞らずに使うと誤判定の温床になる。
 *
 * かつては `navigator.platform === 'MacIntel'` でMacに絞っていたが、
 * `navigator.platform` は非推奨（MDNでは unreliable、TSの型定義でも非推奨マークが付く）。
 * BowserのOS判定で絞れば同じ役割を果たせるため、依存を外した。
 */
function isTouchCapable(signals: DetectOsSignals): boolean {
  return (signals.maxTouchPoints ?? 0) > 1;
}

/**
 * デスクトップ表示を名乗る端末の最終判定。`detectOs` が 'other' / 'unknown' を返した後に使う。
 *
 * **この関数は自己完結させること。** `storeRedirect.ts` は `toString()` でこの関数を
 * そのまま `<script>` に埋め込み、サーバから返すHTMLの中で実行する。
 * 外側の定数・import・クラスを参照すると、埋め込み先で `ReferenceError` になる。
 * （同じ判定を手書きで複製していた結果、片方だけ直され続けたのが従来の不具合の温床だった）
 *
 * 判別できるのは `maxTouchPoints` を読めるクライアントだけで、サーバでは不可能。
 *
 * - **Macintosh を名乗るなら App Store。** iPadOS 13以降のSafariは既定でデスクトップ表示になり
 *   `Macintosh` を名乗る。本物のMacと見分ける必要はない。Apple Silicon の Mac は
 *   App Store から iPhone / iPad アプリを入れられ、仮に非対応でも Play Store よりは正しい。
 * - **Linux を名乗るのにタッチできるなら Play Store。** Chrome for Android の「PC版サイトを見る」は
 *   UAから `Android` トークンを落として `X11; Linux x86_64` を名乗る。`Sec-CH-UA-Platform` も
 *   同時に偽装されるためサーバ側では救えない（実際にこれで本番の遷移を取りこぼした）。
 *   本物のLinuxデスクトップは `maxTouchPoints` が 0 になるのでここで分かれる。
 *   ChromeOS もこの枝に入るが、Play Store からAndroidアプリを入れられるので送って問題ない。
 * - **それ以外（Windows / 判定不能）は `null`。** Androidと決めつけると、Android端末を
 *   持たない利用者を Play Store の行き止まりに送ることになる。推測せず呼び出し側に委ねる。
 *
 * @returns 送り先として確からしいOS。判断が付かなければ `null`
 */
export function detectOsFromDesktopMode(userAgent: string, maxTouchPoints: number): 'ios' | 'android' | null {
  if (/Macintosh/i.test(userAgent)) {
    return 'ios';
  }
  // タッチ点数は1点だと誤検出しやすいため、複数点を要求する
  if (/X11|Linux/i.test(userAgent) && !/Android/i.test(userAgent) && maxTouchPoints > 1) {
    return 'android';
  }
  return null;
}

/**
 * 端末のOSを判定する。
 *
 * 判定順は「確実なものから曖昧なものへ」。曖昧な推測を先に評価すると、
 * 誤爆した時点で確実な判定に到達できなくなる（これが従来の誤判定の原因だった）。
 *
 *   1. Client Hints         … 最も偽装されにくい
 *   2. UA文字列（Bowser）    … Androidトークンは明示的で誤爆しにくい
 *   3. iPadOSヒューリスティック … macOSと判定された場合にのみ適用する推測
 *   4. 判定不能             … 'unknown'。推測せず呼び出し側にフォールバックさせる
 */
export function detectOs(signals: DetectOsSignals): DetectedOs {
  const byClientHints = detectByClientHints(signals.clientHintsPlatform);
  if (byClientHints) {
    return byClientHints;
  }

  switch (detectByUserAgent(signals.userAgent)) {
    case 'android':
      return 'android';
    case 'ios':
      return 'ios';
    case 'macos':
      // iPadOS のデスクトップ表示はここに来る。タッチできるならMacではない。
      return isTouchCapable(signals) ? 'ios' : 'other';
    case 'other':
      return 'other';
    default:
      // null（UAが空、またはBowserがOSを特定できなかった）。
      // この場合はタッチ判定にも進まない。macOSと判ってすらいない状態で
      // maxTouchPoints だけで推測すると誤爆の温床になるため。
      return 'unknown';
  }
}

/**
 * 以下は判定結果を問い合わせるための述語。
 *
 * 呼び出し側の多くは型の付かない JavaScript（`<script>` のままの Vue ページなど）であり、
 * `os === 'andriod'` のような綴り間違いが黙って false になってしまう。
 * 比較を関数に閉じ込めることで、呼び出し側にリテラルを書かせない。
 */
export function isIos(os: DetectedOs): boolean {
  return os === 'ios';
}

export function isAndroid(os: DetectedOs): boolean {
  return os === 'android';
}

/** ストアへの出し分け対象か（= iOS または Android か）。それ以外は呼び出し側で何もしない想定 */
export function isMobileOs(os: DetectedOs): boolean {
  return isIos(os) || isAndroid(os);
}

/** ブラウザの `navigator` からシグナルを集めて判定する。クライアント側の呼び出し口。 */
export function detectOsFromBrowser(): DetectedOs {
  if (typeof window === 'undefined' || !window.navigator) {
    return 'unknown';
  }

  const navigator = window.navigator as Navigator & {
    userAgentData?: { platform?: string };
  };

  return detectOs({
    userAgent: navigator.userAgent || '',
    clientHintsPlatform: navigator.userAgentData?.platform,
    maxTouchPoints: navigator.maxTouchPoints
  });
}
